import React, { useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCsv } from '../../utils/exportCsv';
import type { BackOfficeEngagementRow } from '@crewcast/shared';

function monthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
}

export default function BackOfficeEngagementPage() {
  const { token } = useAuth();
  const now = new Date();
  const [rows, setRows] = useState<BackOfficeEngagementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStart(now));
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));
  const [companies, setCompanies] = useState<{ companyId: string; displayName: string }[]>([]);
  const [companyId, setCompanyId] = useState('');

  // Load company list once for the filter dropdown
  useEffect(() => {
    async function loadCompanies() {
      try {
        if (!token) return;
        const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?pageSize=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) setCompanies(data.companies || []);
      } catch {
        // Non-fatal
      }
    }
    loadCompanies();
  }, []);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      if (!token) throw new Error('Not authenticated');

      const params = new URLSearchParams({ dateFrom, dateTo });
      if (companyId) params.set('companyId', companyId);

      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/engagement?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load engagement data');
      setRows(data.rows || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  }

  function handleApply(e: React.FormEvent) {
    e.preventDefault();
    fetchData();
  }

  function handleExport() {
    exportToCsv(
      `crewcast-engagement-${dateFrom}-${dateTo}`,
      ['companyId', 'displayName', 'mauCount', 'momentsTotal', 'momentsActive', 'momentsInactive', 'submissionsInRange'],
      rows as unknown as Record<string, unknown>[]
    );
  }

  const totalMAU = rows.reduce((s, r) => s + r.mauCount, 0);
  const totalMoments = rows.reduce((s, r) => s + r.momentsTotal, 0);
  const totalSubs = rows.reduce((s, r) => s + r.submissionsInRange, 0);

  return (
    <>
      <div className="bo-page-header">
        <h1 className="bo-page-title">Engagement</h1>
        <p className="bo-page-subtitle">Active users and Moments activity by date range</p>
      </div>

      {/* Summary */}
      <div className="bo-summary-row">
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">MAU (active submitters)</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalMAU}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Total Moments</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalMoments}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Submissions in Range</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalSubs}</div>
        </div>
      </div>

      {/* Filters */}
      <form className="bo-filters" onSubmit={handleApply}>
        <div className="bo-filter-group">
          <label className="bo-filter-label">From</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">To</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Account</label>
          <select
            className="bo-filter-select crewcast-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">All accounts</option>
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
            ))}
          </select>
        </div>

        <div className="bo-filter-actions">
          <button type="submit" className="bo-btn bo-btn-primary" disabled={loading}>
            Apply
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} />
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={handleExport} disabled={loading || rows.length === 0}>
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">
            Engagement by Account
            <span className="bo-table-count">({rows.length})</span>
          </span>
        </div>

        {loading ? (
          <div className="bo-loading">Loading...</div>
        ) : error ? (
          <div className="bo-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="bo-empty">No data for the selected range.</div>
        ) : (
          <div className="bo-table-wrapper">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="num">MAU</th>
                  <th className="num">Submissions</th>
                  <th className="num">Moments Total</th>
                  <th className="num">Active</th>
                  <th className="num">Inactive</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.companyId}>
                    <td>{r.displayName}</td>
                    <td className="num">
                      <strong>{r.mauCount}</strong>
                    </td>
                    <td className="num">{r.submissionsInRange}</td>
                    <td className="num">{r.momentsTotal}</td>
                    <td className="num">
                      {r.momentsActive > 0 ? (
                        <span className="bo-badge bo-badge-green">{r.momentsActive}</span>
                      ) : (
                        <span>0</span>
                      )}
                    </td>
                    <td className="num">{r.momentsInactive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
